import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FileText, Download, Calendar, ArrowRight, ExternalLink, 
  Users, Scale, Shield, Banknote, Heart, ShoppingCart, ClipboardList, Mail
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { CHARITY_NUMBER, REGISTERED_CHARITY_LABEL } from '../constants/charity';
import { PHONE_NUMBER, PHONE_TEL_HREF } from '../constants/contact';
import { scheduleScrollToElementById } from '../constants/scrollNavigation';
import { sortNewsByDateDesc } from '../utils/newsArticle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOVERNANCE_HASH_SCROLL_IDS = ['governance-intro', 'annual-reports'];

// Leadership Team
const leadershipTeam = [
  {
    role: 'President',
    name: 'Dr Sarah Furness PhD',
    description: 'Provides strategic oversight and represents CPRE at regional and national level.',
    image: '/images/governance/img1.png?v=3',
  },
  {
    role: 'Chair',
    name: 'Ron Simpson BEM',
    description: 'Leads the Board of Trustees and chairs meetings.',
    image: '/images/governance/img2.png?v=3',
    imageCaption: 'Ron Simpson BEM (left) with CPRE National Chair Simon Murray',
  },
  {
    role: 'Vice Chair',
    name: 'Rachel Burkitt',
    description: 'Supports the Chair and deputises when required.',
    image: '/images/governance/img3.png?v=3',
    imageCaption: 'Rachel Burkitt (left) with CPRE Chief Executive Roger Mortlock',
  },
  {
    role: 'Planning Lead',
    name: 'Carolyn Cartwright',
    description: 'Leads planning policy responses and development monitoring across Rutland and Lincolnshire.',
    image: '/images/governance/img4.png?v=3',
  },
  {
    role: 'Planning Support',
    name: 'Cliff Bacon',
    description: 'Supports the planning team with application reviews and local development monitoring.',
    image: '/images/governance/img5.png?v=3',
  },
  {
    role: 'Research Officer',
    name: 'Malcolm Touchin',
    description: 'Leads research initiatives and provides evidence-based analysis to support CPRE campaigns and planning responses.',
    image: '/images/governance/img6.png?v=3',
    imageCaption: 'Malcolm Touchin at a CPRE event',
  },
  {
    role: 'Treasurer',
    name: 'Margaret Simpson BEM',
    description: 'Oversees financial management, budgeting, and reporting.',
    image: '/images/governance/img7.png?v=3',
  },
  {
    role: 'Administrator',
    name: 'Geoff Clyde',
    description: 'Manages governance administration and compliance.',
    image: '/images/governance/img8.png?v=3',
  },
  {
    role: 'Governance Trustee',
    name: 'Giles Clifton',
    description: 'Serves on the Board of Trustees, providing governance oversight and strategic guidance.',
    image: '/images/governance/img9.png?v=3',
  },
];

const trustees = [
  { name: 'Trustee positions available', note: 'Planning experience preferred' },
];

// Policy Documents
const policies = [
  {
    title: 'Constitution',
    description: 'AGM Approved 19 June 2024',
    icon: Scale,
    href: 'https://cprerutlandandlincs.uk/wp-content/uploads/2024/07/RSCC-Constitution-AGM-Approved-19.6.2024.pdf',
    color: 'bg-cpre-new-yew',
  },
  {
    title: 'Code of Conduct',
    description: 'Board Member Code of Conduct',
    icon: Shield,
    href: 'https://cprerutlandandlincs.uk/wp-content/uploads/2024/07/RSCC-Board-Member-Code-of-Conduct.17.6.2024.pdf',
    color: 'bg-cpre-hill',
  },
  {
    title: 'Conflict of Interest Policy',
    description: 'December 2021 - Version 1',
    icon: Users,
    href: 'https://cprerutlandandlincs.uk/wp-content/uploads/2021/11/CPRE-Rutland-Conflicts-of-Interests-Policy.-December-2021.-V1.pdf',
    color: 'bg-cpre-autumn-leaf',
  },
  {
    title: 'Financial Regulations',
    description: 'December 2021 - Version 1',
    icon: Banknote,
    href: 'https://cprerutlandandlincs.uk/wp-content/uploads/2021/12/CPRE-Rutland-Financial-Regulations-V1-December-2021.pdf',
    color: 'bg-cpre-harvest-gold',
  },
  {
    title: 'Equality & Diversity Policy',
    description: '17 June 2024',
    icon: Heart,
    href: 'https://cprerutlandandlincs.uk/wp-content/uploads/2024/07/RSCC-Equality-and-Diversity-Policy.17.6.2024-002.pdf',
    color: 'bg-cpre-mellow-rose',
  },
  {
    title: 'Procurement Policy',
    description: '19 June 2024',
    icon: ShoppingCart,
    href: 'https://cprerutlandandlincs.uk/wp-content/uploads/2024/07/RSCC-Procurement-Policy-19.6.2024.pdf',
    color: 'bg-cpre-bumblebee',
  },
];

// Meetings & Minutes Documents
const meetingsMinutes = [
  {
    title: 'Annual Report 2024',
    description: 'CPRE Rutland Annual Report',
    href: 'https://cprerutlandandlincs.uk/wp-content/uploads/2024/07/CPRE-Rutland-Annual-Report-2024.Final_.pdf',
    date: '2024',
  },
  {
    title: 'Board Meeting Minutes',
    description: 'Meeting of Charity Trustees - 23 Feb 2024',
    href: 'https://cprerutlandandlincs.uk/wp-content/uploads/2024/07/Minutes-of-Charity-Trustees-23-Feb-24.pdf',
    date: '23 Feb 2024',
  },
];

const DEFAULT_GOVERNANCE_NEWS_IMAGE = '/images/cpre images for website/13.jpg';

// Same API source as NewsPage; local images when API has no governance items or missing image_url
const GOVERNANCE_NEWS_FALLBACK = [
  {
    id: 'trustee-vacancies',
    title: 'CPRE Rutland and Lincolnshire Trustee Vacancies',
    category: 'governance',
    excerpt: 'We are looking for passionate individuals to join our Board of Trustees and help guide the future of countryside protection in Rutland and Lincolnshire.',
    image_url: '/images/cpre images for website/25.jpg',
    created_at: '2025-12-22T00:00:00.000Z',
  },
  {
    id: 'strategic-plan',
    title: 'Branch Strategic Plan',
    category: 'governance',
    excerpt: 'Our new strategic plan outlines our vision and priorities for protecting the countryside over the next five years.',
    image_url: '/images/cpre images for website/17.jpg',
    created_at: '2025-12-22T00:00:00.000Z',
  },
  {
    id: 'annual-report',
    title: 'CPRE Rutland and Lincolnshire Annual Report Published',
    category: 'governance',
    excerpt: 'Our latest annual report details our achievements, financial position, and plans for the future.',
    image_url: '/images/cpre images for website/7.jpg',
    created_at: '2025-06-10T00:00:00.000Z',
  },
  {
    id: 'lincolnshire-trustees',
    title: 'CPRE Rutland and Lincolnshire Branch Seeks Lincolnshire Trustees',
    category: 'governance',
    excerpt: 'Following our expansion into Lincolnshire, we are seeking trustees with local knowledge and expertise.',
    image_url: '/images/cpre images for website/19.jpg',
    created_at: '2025-06-10T00:00:00.000Z',
  },
];

const formatGovernanceNewsDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const GovernancePage = () => {
  const location = useLocation();
  const [governanceLatestNews, setGovernanceLatestNews] = useState(GOVERNANCE_NEWS_FALLBACK);

  useEffect(() => {
    const scrollId = location.hash.slice(1);
    if (!GOVERNANCE_HASH_SCROLL_IDS.includes(scrollId)) return;
    return scheduleScrollToElementById(scrollId, { delayMs: 50 });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const fetchGovernanceNews = async () => {
      try {
        const response = await axios.get(`${API}/news`, { params: { limit: 50 } });
        const governance = sortNewsByDateDesc(response.data || [])
          .filter((article) => article.category === 'governance')
          .slice(0, 4);
        setGovernanceLatestNews(
          governance.length > 0 ? governance : sortNewsByDateDesc(GOVERNANCE_NEWS_FALLBACK)
        );
      } catch (error) {
        console.error('Failed to fetch governance news:', error);
        setGovernanceLatestNews(sortNewsByDateDesc(GOVERNANCE_NEWS_FALLBACK));
      }
    };
    fetchGovernanceNews();
  }, []);

  return (
    <Layout>
      <div data-testid="governance-page">
        {/* Hero Section */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-cpre-new-yew">
          <div className="absolute inset-0 opacity-20">
            <img
              src="/images/cpre images for website/3.jpg"
              alt="Countryside"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative container-custom">
            <div className="max-w-3xl">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
                Governance
              </h1>
              <p className="text-xl text-white/90 leading-relaxed">
                As a registered charity ({REGISTERED_CHARITY_LABEL}), CPRE Rutland and Lincolnshire is required to operate in accordance with its 
                Constitution and Charity Commission regulations. Information on how the charity is governed, and by whom, 
                must be in the public domain.
              </p>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section id="governance-intro" className="section-padding">
          <div className="container-custom">
            <div className="max-w-4xl">
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-cpre-new-yew/80 leading-relaxed mb-6">
                  In the interests of transparency, the Charity has decided to publish its board minutes, 
                  operational policies, and financial regulations. All documents are available for public 
                  access below.
                </p>
                <div className="flex flex-wrap gap-4 p-6 bg-cpre-pebble/30 rounded-2xl">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-cpre-new-yew/70">Registered Charity No:</span>
                    <span className="font-semibold text-cpre-new-yew">{CHARITY_NUMBER}</span>
                  </div>
                  <div className="hidden sm:block w-px h-6 bg-cpre-new-yew/20" />
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-cpre-new-yew/70">Branch:</span>
                    <span className="font-semibold text-cpre-new-yew">Rutland and Lincolnshire</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Leadership Section */}
        <section className="section-padding bg-cpre-pebble/30" data-testid="leadership-section">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-cpre-new-yew mb-4">
                Leadership & Trustees
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Our dedicated team of volunteers leads the charity's strategic direction and operations.
              </p>
            </div>

            {/* Leadership Roles */}
            <div className="max-w-4xl mx-auto mb-12">
              <h3 className="font-heading text-xl font-semibold text-cpre-new-yew mb-6">
                Leadership Team
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {leadershipTeam.map((member, index) => (
                  <Card 
                    key={index} 
                    className={`bg-white border ${member.isVacancy ? 'border-cpre-autumn-leaf border-2' : 'border-stone-200'} ${member.image ? 'overflow-hidden' : ''}`}
                  >
                    {member.image && (
                      <div className="aspect-square overflow-hidden relative">
                        <img 
                          src={member.image} 
                          alt={member.name}
                          className="w-full h-full object-cover object-top"
                        />
                        {member.imageCaption && (
                          <div className="absolute bottom-0 left-0 right-0 bg-cpre-new-yew/80 px-3 py-2">
                            <p className="text-white text-xs">{member.imageCaption}</p>
                          </div>
                        )}
                      </div>
                    )}
                    <CardContent className="p-6">
                      {member.isVacancy && (
                        <Badge className="bg-cpre-autumn-leaf text-white mb-3">
                          Vacancy
                        </Badge>
                      )}
                      <h4 className="font-heading font-semibold text-lg text-cpre-new-yew mb-1">
                        {member.role}
                      </h4>
                      <p className={`text-sm mb-2 ${member.isVacancy ? 'text-cpre-autumn-leaf font-medium' : 'text-cpre-new-yew/80 font-medium'}`}>
                        {member.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {member.description}
                      </p>
                      {member.isVacancy && (
                        <a href="mailto:chair@cprerutlandandlincs.uk?subject=Treasurer Vacancy">
                          <Button size="sm" className="mt-4 bg-cpre-autumn-leaf hover:bg-cpre-autumn-leaf/90 text-white rounded-full text-xs">
                            Apply Now
                          </Button>
                        </a>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Trustee Recruitment Notice */}
            <div className="max-w-4xl mx-auto">
              <div className="p-6 bg-cpre-bumblebee/20 border border-cpre-bumblebee rounded-2xl">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <h4 className="font-heading font-semibold text-cpre-new-yew mb-2">
                      Become a Trustee
                    </h4>
                    <p className="text-sm text-cpre-new-yew/80">
                      We are seeking new trustees, particularly those with planning experience, 
                      to help guide our work across Rutland and Lincolnshire.
                    </p>
                  </div>
                  <Link to="/volunteering">
                    <Button className="bg-cpre-new-yew hover:bg-cpre-new-yew/90 text-white rounded-full whitespace-nowrap">
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Latest Governance News */}
        <section className="section-padding bg-cpre-pebble/30">
          <div className="container-custom">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-cpre-new-yew">
                Latest Governance News
              </h2>
              <Link to="/news?category=governance">
                <Button variant="outline" className="border-cpre-new-yew text-cpre-new-yew hover:bg-cpre-new-yew hover:text-white rounded-full">
                  View All
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {governanceLatestNews.map((item, index) => (
                <Card 
                  key={item.id}
                  className="bg-white border-0 overflow-hidden group hover:shadow-lg transition-all duration-300"
                  data-testid={`governance-news-${index}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={item.image_url || item.image || DEFAULT_GOVERNANCE_NEWS_IMAGE}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className="bg-cpre-new-yew/10 text-cpre-new-yew text-xs hover:bg-cpre-autumn-leaf hover:text-white">
                        {item.category === 'governance' ? 'Governance' : item.category}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {item.created_at ? formatGovernanceNewsDate(item.created_at) : item.date}
                      </span>
                    </div>
                    <h4 className="font-heading font-semibold text-cpre-new-yew line-clamp-2 group-hover:text-cpre-hill transition-colors">
                      {item.title}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                      {item.excerpt}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Policies Section */}
        <section className="section-padding" data-testid="policies-section">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-cpre-new-yew mb-4">
                Policies
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Our operational policies ensure transparent and accountable governance of the charity.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {policies.map((policy, index) => (
                <a
                  key={index}
                  href={policy.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                  data-testid={`policy-${index}`}
                >
                  <Card className="bg-white border border-stone-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full">
                    <div className={`${policy.color} p-6 flex items-center justify-center`}>
                      <policy.icon className="w-16 h-16 text-white" />
                    </div>
                    <CardContent className="p-6">
                      <h4 className="font-heading font-semibold text-lg text-cpre-new-yew mb-2 group-hover:text-cpre-hill transition-colors">
                        {policy.title}
                      </h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        {policy.description}
                      </p>
                      <div className="flex items-center gap-2 text-cpre-new-yew text-sm font-medium">
                        <Download className="w-4 h-4" />
                        Download PDF
                      </div>
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Meetings & Minutes Section */}
        <section id="annual-reports" className="section-padding bg-cpre-heritage-white" data-testid="meetings-section">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-cpre-new-yew mb-4">
                Meetings & Minutes
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Board meeting minutes and annual reports are published for transparency.
              </p>
            </div>

            <div className="max-w-2xl mx-auto">
              <div className="space-y-4">
                {meetingsMinutes.map((doc, index) => (
                  <a
                    key={index}
                    href={doc.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-5 bg-white border border-stone-200 rounded-xl hover:shadow-lg hover:border-cpre-new-yew transition-all duration-300 group"
                    data-testid={`meeting-doc-${index}`}
                  >
                    <div className="w-14 h-14 bg-cpre-new-yew rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-cpre-hill transition-colors">
                      <ClipboardList className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-heading font-semibold text-cpre-new-yew group-hover:text-cpre-hill transition-colors">
                        {doc.title}
                      </h4>
                      <p className="text-sm text-muted-foreground">{doc.description}</p>
                    </div>
                    <div className="flex items-center gap-2 text-cpre-new-yew">
                      <span className="text-sm hidden sm:inline">{doc.date}</span>
                      <Download className="w-5 h-5" />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Trustee CTA Section */}
        <section className="section-padding bg-cpre-new-yew">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center">
              <Users className="w-16 h-16 text-cpre-harvest-gold mx-auto mb-6" />
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
                Interested in Becoming a Trustee?
              </h2>
              <p className="text-white/80 text-lg mb-8">
                We are always looking for passionate individuals to join our Board of Trustees. 
                If you care about protecting the countryside of Rutland and Lincolnshire, 
                we'd love to hear from you.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/volunteering">
                  <Button size="lg" className="bg-cpre-harvest-gold hover:bg-cpre-harvest-gold/90 text-cpre-new-yew rounded-full px-8">
                    Learn About Opportunities & Volunteering
                  </Button>
                </Link>
                <a href="mailto:chair@cprerutlandandlincs.uk">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-cpre-new-yew rounded-full px-8">
                    Email Us
                  </Button>
                </a>
                <a href={PHONE_TEL_HREF}>
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-cpre-new-yew rounded-full px-8">
                    {PHONE_NUMBER}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default GovernancePage;
